import React from "react";
import {
  Card,
  Text,
  Button,
  SimpleGrid,
  Group,
  Box,
  createStyles,
  Title,
  Divider,
} from "@mantine/core";
import { IconFileDescription } from "@tabler/icons-react";
import { formatDateTime } from "@medplum/core";

interface ProviderData {
  name: string;
  providerName: string;
  practiceType: string;
  requestId: string;
  backOffice: string;
  status: string;
  patientName: any;
  subType: any;
  created: any;
  insurer: any;
  claimId: any;
  provider: any;
  outcome:any
}

interface ProviderCardProps {
  data: ProviderData;
  labels?: {
    providerName?: string;
    practiceType?: string;
    requestId?: string;
    backOffice?: string;
  };
  handleDetails: (requestId: string) => void;
}

const useStyles = createStyles(() => ({
  cardHeading: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    marginBottom: "25px",
  },
  statusBadge: {
    textTransform: "uppercase",
    backgroundColor: "#f5faff",
    borderRadius: "5px",
    color: "#2692ff",
    display: "flex",
    capitalize: "none",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "60px",
    height: "30px",
  },
  cardDetails: {
    backgroundColor: "#F1F9FF",
    borderRadius: "8px",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
  },
  fieldName: {
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "20px",
    color: "#474E5B",
  },
  fieldValue: {
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "20px",
    color: "#262930",
  },
}));

const ProviderCard: React.FC<ProviderCardProps> = ({
  data,
  labels,
  handleDetails,
}) => {
   
  const { classes } = useStyles();

  // Default field labels
  const defaultLabels = {
    providerName: "Provider Name",
    practiceType: "Practice Type",
    requestId: "Request Id",
    backOffice: "Back Office",
  };

  // Use the provided labels, or fall back to defaults
  const fieldLabels = {
    providerName: labels?.providerName || defaultLabels.providerName,
    practiceType: labels?.practiceType || defaultLabels.practiceType,
    requestId: labels?.requestId || defaultLabels.requestId,
    backOffice: labels?.backOffice || defaultLabels.backOffice,
  };

  return (
    <Card padding="lg" radius="md" withBorder>
      <Box className={classes.cardHeading}>
        <Title size={18} weight={600} color="#262930">
          {data.name ?? data.patientName}
        </Title>
        <Box
          className={classes.statusBadge}
          style={
            data.status !== "Active"
              ? { backgroundColor: "#fce9e5", color: "#fc6b2d" }
              : {}
          }
        >
          <Text style={{ marginInline: "10px" }}>
            {data.status ?? data.outcome}
          </Text>
        </Box>
      </Box>

      <Box className={classes.cardDetails}>
        <SimpleGrid cols={2} spacing={"28%"}>
          <SimpleGrid ml={10} cols={1} spacing={10}>
            <Text className={classes.fieldName} size="sm" fw={500}>
              {fieldLabels.providerName}
            </Text>
            <Text size="sm">{data.providerName ?? data?.insurer}</Text>
          </SimpleGrid>
          <SimpleGrid cols={1} spacing={1}>
            <Text className={classes.fieldName} size="sm" fw={500}>
              {fieldLabels.practiceType}
            </Text>
            <Text size="sm">
              {data?.practiceType?.slice(0, 20) ?? data?.subType}
            </Text>
          </SimpleGrid>
        </SimpleGrid>
        <Divider my="sm" ml="sm" color="lightgray" w="90%" />
        <SimpleGrid cols={2} spacing={"28%"}>
          <SimpleGrid ml={10} cols={1} spacing={10}>
            <Text className={classes.fieldName} size="sm" fw={500}>
              {fieldLabels.requestId}
            </Text>
            <Text size="sm">
              {data?.requestId?.slice(0, 20)?data?.requestId?.slice(0, 20)+'...' :data?.provider}
            </Text>
          </SimpleGrid>
          <SimpleGrid cols={1} spacing={1}>
            <Text className={classes.fieldName} size="sm" fw={500}>
              {fieldLabels.backOffice}
            </Text>
            <Text size="sm">
              {data.backOffice ?? formatDateTime(data?.created)}
            </Text>
          </SimpleGrid>
        </SimpleGrid>
      </Box>

      <Button
        variant="outline"
        color="#1B75BC"
        fullWidth
        mt="md"
        onClick={() => handleDetails(data.requestId || data.claimId)}
      >
        <IconFileDescription
          size={14}
          strokeWidth={2}
          style={{ marginRight: "5px" }}
        />{" "}
        <Text size={12} color="#1B75BC" weight={600}>
          View Details{" "}
        </Text>
      </Button>
    </Card>
  );
};

interface ProviderCardsProps {
  providers: ProviderData[];
  labels?: {
    providerName?: string;
    practiceType?: string;
    requestId?: string;
    backOffice?: string;
  };
  handleDetails: (requestId: string) => void;
}

const ProviderCards: React.FC<ProviderCardsProps> = ({
  providers,
  labels,
  handleDetails,
}) => (
  <SimpleGrid cols={2} spacing="md" verticalSpacing="xl">
    {providers.map((provider, index) => (
      <ProviderCard
        key={index}
        data={provider}
        labels={labels}
        handleDetails={handleDetails}
      />
    ))}
  </SimpleGrid>
);

export default ProviderCards;
